const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[c]));
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Snapshot live values, not React's initial HTML attributes. Preserve checklist
// state explicitly because native/Radix controls do not print consistently.
export function snapshotSection(section) {
  const clone = section.cloneNode(true);
  const selector = "input, textarea, select, [role=checkbox], [role=combobox]";
  const originals = [...section.querySelectorAll(selector)];
  [...clone.querySelectorAll(selector)].forEach((node, i) => {
    const source = originals[i];
    if (source.type === "hidden") { node.remove(); return; }
    const output = document.createElement("span");
    output.className = "export-value";
    if (source.type === "checkbox" || source.getAttribute("role") === "checkbox") {
      output.textContent = (source.checked || source.getAttribute("aria-checked") === "true") ? "[X] " : "[ ] ";
    } else if (source.tagName === "SELECT") {
      output.textContent = source.selectedOptions[0]?.text || "Not entered";
    } else {
      output.textContent = source.getAttribute("role") === "combobox" ? source.textContent : source.value || "Not entered";
    }
    node.replaceWith(output);
  });
  clone.querySelectorAll("[data-export-controls], script, iframe, button:not([role]), [role=tablist]").forEach((node) => {
    // Collapsible headers are buttons: keep their descriptive content.
    if (node.tagName === "BUTTON" && node.querySelector("h2,h3")) {
      const div = document.createElement("div");
      div.innerHTML = node.innerHTML;
      node.replaceWith(div);
    } else node.remove();
  });
  clone.querySelectorAll("[onclick], [onerror], [onload]").forEach((node) => {
    node.removeAttribute("onclick"); node.removeAttribute("onerror"); node.removeAttribute("onload");
  });
  return clone;
}

export async function exportSection(section, { title, format, disabled }) {
  if (!section || disabled || document.body.classList.contains("is-demo")) throw new Error("Export is unavailable.");
  // Expand collapsed panels before capture, preserving their state afterward.
  const expanded = [];
  let frame;
  try {
    for (let pass = 0; pass < 3; pass++) {
      const toggles = [...section.querySelectorAll("button")].filter((b) =>
        !b.closest("[data-export-controls]") && b.getAttribute("role") !== "combobox" && !b.hasAttribute("aria-haspopup") &&
        (b.getAttribute("aria-expanded") === "false" || /(?:▼.*expand|▼\s*$|expand\s*$)/i.test(b.textContent)));
      if (!toggles.length) break;
      toggles.forEach((b) => { b.click(); expanded.push(b); });
      await wait(350);
    }
    const clone = snapshotSection(section);
    const heading = section.querySelector("h2,h3")?.textContent || section.getAttribute("data-testid") || "Formula section";
    frame = document.createElement("iframe");
    frame.title = "Section export";
    frame.style.cssText = "position:fixed;left:-12000px;top:0;width:820px;height:1100px;border:0";
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    const styles = [...document.querySelectorAll('style,link[rel="stylesheet"]')].map((s) => s.outerHTML).join("");
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)} — ${escapeHtml(heading)}</title>${styles}
      <style>body{background:white!important;color:#111;margin:0;font-family:Arial,sans-serif}
      #export-paper{width:780px;padding:24px;box-sizing:border-box;background:white}
      .export-value{display:inline-block;white-space:pre-wrap;overflow-wrap:anywhere;max-width:100%;padding:3px 5px;color:#111}
      [data-export-section]{box-shadow:none!important;position:static!important}
      .overflow-x-auto,.overflow-auto{overflow:visible!important}
      textarea{height:auto} table{width:100%;table-layout:auto} th,td{overflow-wrap:anywhere}
      tr{break-inside:avoid} h2,h3{break-after:avoid}
      @page{size:letter;margin:12mm} @media print{#export-paper{width:100%;padding:0}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body><main id="export-paper"><h1 style="font-size:19px;font-weight:bold;margin-bottom:8px">${escapeHtml(title || "Alaine's Bench")}</h1>
      <p style="font-size:11px;margin-bottom:16px">${escapeHtml(heading)} · ${escapeHtml(new Date().toLocaleString())}<br>
      Current working values. Calculated guidance only—not safety or regulatory approval. Verify before commercial use.</p>
      ${clone.outerHTML}</main></body></html>`);
    doc.close();
    await Promise.all([...doc.querySelectorAll('link[rel="stylesheet"]')].map((link) =>
      link.sheet ? Promise.resolve() : new Promise((resolve) => { link.onload = resolve; link.onerror = resolve; setTimeout(resolve, 3000); })));
    await doc.fonts?.ready;
    if (format === "print") {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      // Keep iframe alive while native preview reads it (Safari is asynchronous).
      const printedFrame = frame;
      setTimeout(() => printedFrame.remove(), 120000);
      frame = null;
    } else {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
      const canvas = await html2canvas(doc.getElementById("export-paper"), {
        scale: 1.5, backgroundColor: "#ffffff", windowWidth: 820, logging: false,
      });
      const pdf = new jsPDF({ unit: "mm", format: "letter" });
      const width = 191.9, pageHeight = 249.4;
      const maxPixels = Math.floor(pageHeight * canvas.width / width);
      // Prefer a white row near each page end rather than slicing text.
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      let y = 0, page = 0;
      while (y < canvas.height) {
        let end = Math.min(y + maxPixels, canvas.height);
        if (end < canvas.height) {
          for (let row = end; row > end - 100; row--) {
            const pixels = ctx.getImageData(0, row, canvas.width, 1).data;
            let dark = 0;
            for (let p = 0; p < pixels.length; p += 4) if (Math.min(pixels[p], pixels[p + 1], pixels[p + 2]) < 190) dark++;
            if (dark < 5) { end = row; break; }
          }
        }
        const slice = document.createElement("canvas");
        slice.width = canvas.width; slice.height = end - y;
        slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        if (page++) pdf.addPage();
        pdf.addImage(slice.toDataURL("image/png"), "PNG", 12, 12, width, slice.height * width / canvas.width);
        y = end;
      }
      pdf.save(`${`${title || "Bench"}-${heading}`.replace(/[^a-z0-9_-]+/gi, "-").slice(0, 100)}.pdf`);
    }
  } finally {
    frame?.remove();
    expanded.reverse().forEach((button) => { if (button.isConnected) button.click(); });
  }
}